import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import './_subscribe-confirm-modal.scss'
import { Button } from 'components/Button/Button'
import { DropdownCardSelector } from 'components-ref/molecules/wallet/SelectCard/DropdownCardSelector'
import { useModalContext } from 'context/modalContext'
import { AllIcons } from 'helpers/allIcons'
import { useUserProfile } from 'store'
import colors from 'styles/colors'
import { type Campaign } from 'types/types'

interface SubscribeConfirmModalProps {
  subscribeFn: (paymentMethod: {
    method: 'card' | 'deposit'
    cardId: string | undefined
    subscription_plan_id?: string
    promo_campaign_id?: string
  }) => void
  displayName: string
  avatar: string | null
  username: string
  selectedPlan:
    | {
        id: string
        name: string
        price: number
        month_count: number
        discount?: number | null
      }
    | undefined
  campaign: Campaign | null
  /* rebill?: boolean */
  originalPrice: number
  promoCampaignId?: string
}

export const SubscribeConfirmModal = ({
  subscribeFn,
  displayName,
  avatar,
  username,
  selectedPlan,
  campaign,
  /* rebill, */
  originalPrice,
  promoCampaignId
}: SubscribeConfirmModalProps) => {
  const modal = useModalContext()
  const { t } = useTranslation()
  const user = useUserProfile()
  const navigate = useNavigate()
  const [taxRate, setTaxRate] = useState(0) // dollar amount
  const [taxRateFee, setTaxRateFee] = useState<number | null>(null) // percent
  const [paymentMethod, setPaymentMethod] = useState<{
    method: 'card' | 'deposit'
    cardId: string | undefined
  }>({
    method: 'deposit',
    cardId: undefined
  })

  const cardsArr: any[] = Array.isArray((user as any)?.cards)
    ? (user as any).cards
    : []

  const defaultCardId: string | undefined = (() => {
    const raw = (user as any)?.default_card
    return typeof raw === 'string' && raw ? raw : cardsArr[0]?.id
  })()

  const basePrice = Number(
    campaign?.free ? 0 : (selectedPlan?.price ?? 0)
  )
  const isFreePromo =
    campaign?.discounted_price != null &&
    Number(campaign.discounted_price) === 0


  const SHOW_FREE_PROMO_RENEWAL = true

  // Use default card country fee as the proxy for wallet fee% (same assumption as before)
  const defaultCard = cardsArr.find(
    (c: any) => c?.id === defaultCardId
  )
  const walletFeePercent =
    typeof defaultCard?.country?.fee === 'number'
      ? defaultCard.country.fee
      : 0

  const walletTaxAmount = parseFloat(
    (basePrice * (walletFeePercent / 100)).toFixed(2)
  )
  const walletTotalDue = parseFloat(
    (basePrice + walletTaxAmount).toFixed(2)
  )

  const walletBalance = Number(
    (user as any)?.wallet_deposit ?? 0
  )
  const walletCanCover = walletBalance >= basePrice
  const isWalletSelected = paymentMethod.method === 'deposit'
  const showLoadButton =
    isWalletSelected && !walletCanCover

  const showPerMonth =
    Number(selectedPlan?.discount ?? 0) === 0

  // Title should always be the plan name (e.g., "Monthly")
    const titleText = isFreePromo
    ? 'Free Trial'
    : selectedPlan?.name ?? ''

  // Promo/extra copy goes in a smaller subtext line
  const promoSubtext =
    isFreePromo
      ? ''
      : (campaign?.discount ??
          (campaign as any)?.discounted_price)
        ? `First month $${basePrice.toFixed(
            2
          )}, then $${Number(originalPrice).toFixed(
            2
          )} / ${t('monthShort')}`
        : ''

  const selectCard = (next: {
    method: 'card' | 'deposit'
    cardId?: string
  }) => {
    setPaymentMethod(prev => ({
      ...prev,
      ...next
    }))

    if (next.method !== 'card') {
      setTaxRate(0)
      setTaxRateFee(null)
    }
  }

  // Immediate default: wallet if it has funds; else default card (or first card)
  useEffect(() => {
    selectCard({
      method: walletCanCover ? 'deposit' : 'card',
      cardId: walletCanCover
        ? undefined
        : defaultCardId
    })
  }, [walletCanCover, defaultCardId])

  // If card method selected but no card id yet, pick the first available
  useEffect(() => {
    if (paymentMethod.method !== 'card') return
    if (paymentMethod.cardId) return

    const firstId =
      defaultCardId ?? cardsArr[0]?.id

    if (firstId) {
      selectCard({
        method: 'card',
        cardId: firstId
      })
    }
  }, [
    paymentMethod.method,
    paymentMethod.cardId,
    defaultCardId,
    cardsArr
  ])

  // Update taxRateFee based on selected card’s country fee
  useEffect(() => {
    if (
      paymentMethod.method !== 'card' ||
      !paymentMethod.cardId
    ) {
      setTaxRateFee(null)
      return
    }

    const selectedCard = cardsArr.find(
      (c: any) => c?.id === paymentMethod.cardId
    )
    const fee = Number(selectedCard?.tax_rate ?? 0)

    setTaxRateFee(
      typeof fee === 'number' ? fee : null
    )
  }, [
    paymentMethod.method,
    paymentMethod.cardId,
    cardsArr
  ])

  // Compute tax dollar amount
  useEffect(() => {
    if (
      paymentMethod.method !== 'card' ||
      typeof taxRateFee !== 'number'
    ) {
      setTaxRate(0)
      return
    }

    setTaxRate(
      parseFloat(
        (basePrice * (taxRateFee / 100)).toFixed(2)
      )
    )
  }, [
    paymentMethod.method,
    taxRateFee,
    basePrice
  ])

  return (
    <div className="subscribe-confirm-modal-wrapper">
      <InfoWrapper>
        <div className='confirm-avatar'>
          <img
            src={avatar ?? AllIcons.user_placeholder}
            alt={t('profileAvatar')}
          />
        </div>

        <NamesWrapper>
          <DisplayName>{displayName}</DisplayName>
          <UserName>@{username}</UserName>
        </NamesWrapper>
      </InfoWrapper>

      <Subscription>
        <div>
          <SubscriptionDurationLabel>
            {t('subscription')}
          </SubscriptionDurationLabel>

          <SubscriptionDurationText>
            <SubscriptionTitle>
              {titleText}
            </SubscriptionTitle>

            {promoSubtext ? (
              <SubscriptionSubtext>
                {promoSubtext}
              </SubscriptionSubtext>
            ) : (
              (!isFreePromo || SHOW_FREE_PROMO_RENEWAL) && (
                <SubscriptionSubtext className='renews-at'>
                  renews @ ${Number(originalPrice).toFixed(2)}/mo.
                </SubscriptionSubtext>
              )
            )}
          </SubscriptionDurationText>
        </div>

        <PriceTaxColumn>
          <SubscriptionPrice>
            ${basePrice.toFixed(2)}
            {showPerMonth && (
              <PerMonthSpan>/mo</PerMonthSpan>
            )}
          </SubscriptionPrice>

          {basePrice !== 0 &&
            paymentMethod.method === 'card' &&
            typeof taxRateFee === 'number' &&
            taxRateFee > 0 && (
              <TaxRate>
                <TaxRateText>{t('TAX')}</TaxRateText>
                <TaxAmount>
                  + ${taxRate.toFixed(2)}
                </TaxAmount>
              </TaxRate>
            )}
        </PriceTaxColumn>
      </Subscription>

      {/* Pass cards from user directly, preserving DropdownCardSelector’s expected shape */}
      <DropdownCardSelector
        paymentMethod={paymentMethod}
        setPaymentMethod={selectCard}
        cardList={{ cards: cardsArr }}
        walletCanCover={walletCanCover}
        walletTotalDue={walletTotalDue}
      />

      {paymentMethod.method === 'card' && (
        <p className="notice">
          The charge on your credit card bill will
          appear as
          <span className="company-name">
            {' '}
            Innodev Solutions Inc{' '}
          </span>
          on your credit card statement
        </p>
      )}

      <p className="agree-to-terms">
        By subscribing, you agree to AllFans&apos;{' '}
        <a
          href="/terms"
          className='terms-links'
        >
          Terms & Conditions
        </a>{' '}
        and{' '}
        <a
          href="/privacypolicy"
          className='terms-links'
        >
          Privacy Policy
        </a>
        .
      </p>

      <ButtonWrapper>
        <Button
          text={t('cancel')}
          color='grey'
          font='mont-14-normal'
          width='13'
          height='3'
          clickFn={() => modal.clearModal()}
        />

        <Button
          text={
            showLoadButton
              ? 'Load Wallet'
              : t('subscribe')
          }
          color='blue'
          font='mont-14-normal'
          width='13'
          height='3'
          clickFn={() => {
            if (showLoadButton) {
              modal.clearModal()
              navigate('/settings/general/wallet')
              return
            }

            subscribeFn({
              method: paymentMethod.method,
              cardId: paymentMethod.cardId,
              subscription_plan_id: selectedPlan?.id,
              promo_campaign_id: promoCampaignId
            })

            modal.clearModal()
          }}
        />
      </ButtonWrapper>
    </div>
  )
}

/* styled */
const ButtonWrapper = styled.div`
  display: flex; justify-content: flex-end; gap: 1rem; margin-top: auto; padding-bottom: 1.5rem; background: white;
`
const InfoWrapper = styled.div` display: flex; gap: 1.2rem; padding-bottom:1.5rem; align-items:center;`
const NamesWrapper = styled.div` display: flex; flex-direction: column; `
const DisplayName = styled.div` font-weight: 600; font-size: 1.6rem; `
const UserName = styled.div` font-weight: 500; font-size: 1.2rem; color: ${colors.slateBlueGray}; `
const Subscription = styled.div`
  display: flex; justify-content: space-between; align-items: center; padding: 1.6rem; gap: 1rem;
  border-radius: 1.6rem; border: 0.1rem solid rgba(103,110,125,0.2); margin-bottom: 1rem; box-shadow: 0 0.4rem 2rem rgba(0,0,0,0.05);
`
const SubscriptionDurationLabel = styled.div` color: ${colors.slateBlueGray}; font-size: 1.2rem; `
const SubscriptionDurationText = styled.div` display: flex; flex-direction: column; gap: .35rem; `
const SubscriptionTitle = styled.div` font-size: 1.6rem; font-weight: 600; `
const SubscriptionSubtext = styled.div` font-size: 1.0rem; color: ${colors.slateBlueGray}; font-weight: 500; `
const PriceTaxColumn = styled.div` display: flex; flex-direction: column; align-items: flex-end; justify-content: center; min-width: 95px; `
const SubscriptionPrice = styled.div` font-size: 1.6rem; font-weight: 600; text-align: right; display: flex; align-items: baseline; gap: .4rem; `
const PerMonthSpan = styled.span` font-size: 1.2rem; color: #888; font-weight: 400; `
const TaxRate = styled.div` display: flex; align-items: center; margin-top: 4px; font-size: 1.1rem; `
const TaxRateText = styled.div` color: #7d889b; font-weight: 500; `
const TaxAmount = styled.span` margin-left: 5px; font-weight: 600; color: #000; `